import React, { useState } from 'react';
import DisModal from './design-system/DisModal';
import DisCard from './design-system/DisCard';
import DisAvatar from './design-system/DisAvatar';
import DisButton from './design-system/DisButton';

const ChildrenManagementModal = ({ isOpen, onClose, children, onSelectChild, onAddChild, onDeleteChild }) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Couleurs fun pour les cartes d'enfants - Palette bleu/vert
  const childCardColors = [
    'from-blue-400 to-blue-600', // Bleu
    'from-teal-400 to-teal-600', // Teal
    'from-cyan-400 to-cyan-600', // Cyan
    'from-emerald-400 to-emerald-600', // Vert émeraude
  ];

  const handleDeleteClick = (child) => {
    setShowDeleteConfirm(child);
  };

  const handleConfirmDelete = async () => {
    if (!showDeleteConfirm) return;
    
    setIsDeleting(true);
    try {
      await onDeleteChild(showDeleteConfirm.id);
      setShowDeleteConfirm(null);
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(null);
  };

  // Si on est en mode confirmation de suppression
  if (showDeleteConfirm) {
    return (
      <DisModal
        isOpen={isOpen}
        onClose={onClose}
        title="⚠️ Confirmer la suppression"
        size="md"
        transparent={true}
      >
        <div className="space-y-6 text-center p-4">
          <div className="text-6xl mb-4">🗑️</div>
          
          <div className="space-y-4">
            <h3 className="text-xl font-bold text-red-600">
              Êtes-vous sûr(e) de vouloir supprimer le profil de {showDeleteConfirm.name} ?
            </h3>
            
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center justify-center mb-3">
                <DisAvatar
                  name={showDeleteConfirm.name}
                  gender={showDeleteConfirm.gender}
                  size="lg"
                  className="border-2 border-red-300"
                />
              </div>
              <p className="text-red-700 font-medium">
                {showDeleteConfirm.name} • {showDeleteConfirm.age === 1 ? `${showDeleteConfirm.age} an` : `${showDeleteConfirm.age} ans`}
              </p>
            </div>
            
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-yellow-800 text-sm">
                ⚠️ <strong>Attention :</strong> Cette action est irréversible !
              </p>
              <p className="text-yellow-700 text-xs mt-1">
                • Tout l'historique des conversations sera perdu<br/>
                • Les paramètres personnalisés seront effacés<br/>
                • Vous pourrez toujours créer un nouveau profil
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row justify-center space-y-2 sm:space-y-0 sm:space-x-4 pt-4">
            <DisButton
              variant="secondary"
              onClick={handleCancelDelete}
              disabled={isDeleting}
              className="w-full sm:w-auto px-6 py-3"
            >
              🚫 Annuler
            </DisButton>
            <DisButton
              onClick={handleConfirmDelete}
              loading={isDeleting}
              disabled={isDeleting}
              className="w-full sm:w-auto bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 px-6 py-3"
            >
              {isDeleting ? '🗑️ Suppression...' : '✅ Oui, supprimer'}
            </DisButton>
          </div>
        </div>
      </DisModal>
    );
  }

  return (
    <DisModal
      isOpen={isOpen}
      onClose={onClose}
      title="👨‍👩‍👧‍👦 Gestion des enfants"
      size="lg"
      transparent={true}
    >
      <div className="space-y-4 p-2">
        <div className="text-center mb-6">
          <p className="text-gray-600 text-base mb-2">
            Gérez les profils de vos enfants 🎯
          </p>
          <p className="text-gray-500 text-sm">
            Sélectionnez ou supprimez un profil 🌟
          </p>
        </div>

        {/* Grille d'enfants RESPONSIVE avec couleurs */}
        <div className="space-y-3">
          {children.map((child, index) => {
            const colorClass = childCardColors[index % childCardColors.length];
            return (
              <div
                key={child.id}
                className={`rounded-xl p-4 bg-gradient-to-r ${colorClass} shadow-lg`}
              >
                <div className="flex items-center space-x-3">
                  {/* Avatar */}
                  <div className="flex-shrink-0">
                    <DisAvatar
                      name={child.name}
                      gender={child.gender}
                      size="md"
                      className="border-3 border-white shadow-md"
                    />
                  </div>
                  
                  {/* Informations enfant */}
                  <div className="flex-1 min-w-0 text-white">
                    <h3 className="font-bold text-lg truncate">
                      {child.name}
                    </h3>
                    <div className="text-white/90 text-sm">
                      {child.age === 1 ? `${child.age} an` : `${child.age} ans`} • 
                      {child.gender === 'boy' ? ' 🧒 Garçon' : 
                       child.gender === 'girl' ? ' 👧 Fille' : 
                       ' 😊 Neutre'}
                    </div>
                  </div>
                </div>
                
                {/* Actions RESPONSIVE */}
                <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2 mt-3">
                  <DisButton
                    size="sm"
                    onClick={() => onSelectChild(child)}
                    className="flex-1 bg-white/20 hover:bg-white/30 text-white border border-white/30 backdrop-blur-sm py-2 text-sm font-medium"
                  >
                    💬 Sélectionner
                  </DisButton>
                  <DisButton
                    size="sm"
                    onClick={() => handleDeleteClick(child)}
                    className="flex-1 bg-red-500/80 hover:bg-red-600/80 text-white border border-red-400/50 py-2 text-sm font-medium"
                  >
                    🗑️ Supprimer
                  </DisButton>
                </div>
              </div>
            );
          })}
        </div>

        {/* Message si pas d'enfants */}
        {children.length === 0 && (
          <div className="text-center py-8">
            <div className="text-4xl mb-4">👶</div>
            <h3 className="text-lg font-bold text-gray-600 mb-2">
              Aucun profil enfant
            </h3>
            <p className="text-gray-500 text-sm">
              Créez votre premier profil enfant pour commencer !
            </p>
          </div>
        )}

        {/* Bouton ajouter enfant */}
        {children.length < 4 && (
          <div
            className="border-3 border-dashed border-teal-300 hover:border-teal-400 bg-gradient-to-br from-blue-50 to-teal-50 hover:from-blue-100 hover:to-teal-100 cursor-pointer transition-all duration-200 rounded-xl p-4"
            onClick={onAddChild}
          >
            <div className="flex items-center justify-center space-x-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-teal-500 flex items-center justify-center text-white text-lg font-bold shadow-md">
                <span>+</span>
              </div>
              <div className="text-center">
                <h3 className="font-bold text-teal-700 text-base">
                  ➕ Ajouter un enfant
                </h3>
                <p className="text-teal-600 text-xs">
                  Jusqu'à 4 profils magiques ! ✨
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Message limite atteinte */}
        {children.length >= 4 && (
          <div className="text-center p-3 bg-yellow-50 rounded-xl border border-yellow-200">
            <p className="text-yellow-700 font-medium text-sm">
              🎉 Maximum 4 profils enfants atteint ! 
            </p>
            <p className="text-yellow-600 text-xs">
              Supprimez un profil pour en ajouter un nouveau 😊
            </p>
          </div>
        )}

        {/* Footer */}
        <div className="pt-4 border-t border-gray-200">
          <DisButton
            variant="secondary"
            onClick={onClose}
            className="w-full py-3 text-base"
          >
            🚪 Fermer
          </DisButton>
        </div>
      </div>
    </DisModal>
  );
};

export default ChildrenManagementModal;